import {ref} from 'vue'
import {BASE_URL} from "@/composables/url_config"
import {log} from "@/composables/useLog"
import axios from "axios"

const error = ref(null)

const getHeaders = (token) => ({
    "Authorization": `Bearer ${token}`,
    "Content-Type": "application/json",
    "Accept": "application/json"
})

// ================== COMMISSION API ==================

export const getCommissionPolicies = async (token) => {
    log("getCommissionPolicies called")
    if (!token) return {error: true, message: "No token provided", policies: []}

    try {
        const result = await axios.get(BASE_URL + '/mlm/get_commission_policies_api', {headers: getHeaders(token)})
        return result.data
    } catch (err) {
        log("Error in getCommissionPolicies: " + err.message)
        error.value = err.message
        return {error: true, message: "Error loading commission policies", policies: []}
    }
}

export const createCommissionPolicy = async (token, policyData) => {
    log("createCommissionPolicy called")
    if (!token) return {error: true, message: "No token provided"}

    try {
        const result = await axios.post(BASE_URL + '/mlm/create_commission_policy_api', policyData, {headers: getHeaders(token)})
        return result.data
    } catch (err) {
        log("Error in createCommissionPolicy: " + err.message)
        error.value = err.message
        return {error: true, message: "Error creating commission policy"}
    }
}

export const getCommissionBalance = async (token, clientId = null) => {
    log("getCommissionBalance called")
    if (!token) return {error: true, message: "No token provided", balance: null}

    try {
        const params = clientId ? {client_id: clientId} : {}
        const result = await axios.get(BASE_URL + '/mlm/get_commission_balance_api', {headers: getHeaders(token), params})
        return result.data
    } catch (err) {
        log("Error in getCommissionBalance: " + err.message)
        error.value = err.message
        return {error: true, message: "Error loading commission balance", balance: null}
    }
}

export const getCommissionTransactions = async (token, options = {}) => {
    log("getCommissionTransactions called")
    if (!token) return {error: true, message: "No token provided", transactions: [], total: 0}

    try {
        const params = {}
        if (options.client_id) params.client_id = options.client_id
        if (options.status) params.status = options.status
        if (options.from_date) params.from_date = options.from_date
        if (options.to_date) params.to_date = options.to_date
        if (options.page) params.page = options.page
        if (options.per_page) params.per_page = options.per_page

        const result = await axios.get(BASE_URL + '/mlm/get_commission_transactions_api', {headers: getHeaders(token), params})
        return result.data
    } catch (err) {
        log("Error in getCommissionTransactions: " + err.message)
        error.value = err.message
        return {error: true, message: "Error loading transactions", transactions: [], total: 0}
    }
}

export const requestCommissionPayout = async (token, amount, payoutMethod = 'bank_transfer', bankDetails = null) => {
    log("requestCommissionPayout called")
    if (!token) return {error: true, message: "No token provided"}

    try {
        const payload = {amount, payout_method: payoutMethod}
        if (bankDetails) payload.bank_details = bankDetails

        const result = await axios.post(BASE_URL + '/mlm/request_commission_payout_api', payload, {headers: getHeaders(token)})
        return result.data
    } catch (err) {
        log("Error in requestCommissionPayout: " + err.message)
        error.value = err.message
        return {error: true, message: "Error requesting payout"}
    }
}

// ================== REFERRAL API ==================

export const getMyReferralLinks = async (token, clientId = null, includeSubclients = false) => {
    log("getMyReferralLinks called")
    if (!token) return {error: true, message: "No token provided", referral_links: []}

    try {
        const params = {}
        if (clientId) params.client_id = clientId
        if (includeSubclients) params.include_subclients = true
        const result = await axios.get(BASE_URL + '/mlm/get_my_referral_links_api', {headers: getHeaders(token), params})
        return result.data
    } catch (err) {
        log("Error in getMyReferralLinks: " + err.message)
        error.value = err.message
        return {error: true, message: "Error loading referral links", referral_links: []}
    }
}

export const createReferralLink = async (token, linkData) => {
    log("createReferralLink called")
    if (!token) return {error: true, message: "No token provided"}

    try {
        const result = await axios.post(BASE_URL + '/mlm/create_referral_link_api', linkData, {headers: getHeaders(token)})
        return result.data
    } catch (err) {
        log("Error in createReferralLink: " + err.message)
        error.value = err.message
        return {error: true, message: "Error creating referral link"}
    }
}

export const updateReferralLink = async (token, linkId, linkData) => {
    log("updateReferralLink called")
    if (!token) return {error: true, message: "No token provided"}

    try {
        const result = await axios.put(BASE_URL + '/mlm/update_referral_link_api', {...linkData, id: linkId}, {headers: getHeaders(token)})
        return result.data
    } catch (err) {
        log("Error in updateReferralLink: " + err.message)
        error.value = err.message
        return {error: true, message: "Error updating referral link"}
    }
}

export const getMyReferrals = async (token, clientId = null, includeSubclients = false) => {
    log("getMyReferrals called")
    if (!token) return {error: true, message: "No token provided", referrals: [], stats: null}

    try {
        const params = {}
        if (clientId) params.client_id = clientId
        if (includeSubclients) params.include_subclients = true
        const result = await axios.get(BASE_URL + '/mlm/get_my_referrals_api', {headers: getHeaders(token), params})
        return result.data
    } catch (err) {
        log("Error in getMyReferrals: " + err.message)
        error.value = err.message
        return {error: true, message: "Error loading referrals", referrals: [], stats: null}
    }
}

export const validateReferralCode = async (token, referralCode) => {
    log("validateReferralCode called")
    if (!token) return {error: true, message: "No token provided"}

    try {
        const result = await axios.post(BASE_URL + '/mlm/validate_referral_code_api', {referral_code: referralCode}, {headers: getHeaders(token)})
        return result.data
    } catch (err) {
        log("Error in validateReferralCode: " + err.message)
        error.value = err.message
        return {error: true, message: "Error validating referral code", valid: false}
    }
}

// ================== DISCOUNT API ==================

export const applyDiscountCode = async (token, discountCode, amount, clientId = null) => {
    log("applyDiscountCode called")
    if (!token) return {error: true, message: "No token provided"}

    try {
        const payload = {discount_code: discountCode, amount}
        if (clientId) payload.client_id = clientId

        const result = await axios.post(BASE_URL + '/mlm/apply_discount_code_api', payload, {headers: getHeaders(token)})
        return result.data
    } catch (err) {
        log("Error in applyDiscountCode: " + err.message)
        error.value = err.message
        return {error: true, message: "Error applying discount"}
    }
}

export const getMyDiscounts = async (token, clientId = null) => {
    log("getMyDiscounts called")
    if (!token) return {error: true, message: "No token provided", discounts: []}

    try {
        const params = clientId ? {client_id: clientId} : {}
        const result = await axios.get(BASE_URL + '/mlm/get_my_discounts_api', {headers: getHeaders(token), params})
        return result.data
    } catch (err) {
        log("Error in getMyDiscounts: " + err.message)
        error.value = err.message
        return {error: true, message: "Error loading discounts", discounts: []}
    }
}

// ================== MLM RANK API ==================

export const getMlmRanks = async (token) => {
    log("getMlmRanks called")
    if (!token) return {error: true, message: "No token provided", ranks: []}

    try {
        const result = await axios.get(BASE_URL + '/mlm/get_mlm_ranks_api', {headers: getHeaders(token)})
        return result.data
    } catch (err) {
        log("Error in getMlmRanks: " + err.message)
        error.value = err.message
        return {error: true, message: "Error loading ranks", ranks: []}
    }
}

export const getMyRank = async (token, clientId = null) => {
    log("getMyRank called")
    if (!token) return {error: true, message: "No token provided", current_rank: null, progress: null}

    try {
        const params = clientId ? {client_id: clientId} : {}
        const result = await axios.get(BASE_URL + '/mlm/get_my_rank_api', {headers: getHeaders(token), params})
        return result.data
    } catch (err) {
        log("Error in getMyRank: " + err.message)
        error.value = err.message
        return {error: true, message: "Error loading rank", current_rank: null, progress: null}
    }
}

const useMlmAPI = () => {
    return {
        error,
        // Commission
        getCommissionPolicies,
        createCommissionPolicy,
        getCommissionBalance,
        getCommissionTransactions,
        requestCommissionPayout,
        // Referrals
        getMyReferralLinks,
        createReferralLink,
        updateReferralLink,
        getMyReferrals,
        validateReferralCode,
        // Discounts
        applyDiscountCode,
        getMyDiscounts,
        // Ranks
        getMlmRanks,
        getMyRank
    }
}

export default useMlmAPI
